import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { ComparativeBenchmark } from '@coffeeplexity/shared';

interface BenchmarkComparisonProps {
  benchmark: ComparativeBenchmark;
}

export const BenchmarkComparison: React.FC<BenchmarkComparisonProps> = ({
  benchmark,
}) => {
  const getPerformanceColor = () => {
    if (benchmark.percentileRank >= 80) return '#10B981';
    if (benchmark.percentileRank >= 60) return '#F59E0B';
    return '#EF4444';
  };

  const getPerformanceIcon = () => {
    if (benchmark.percentileRank >= 80) return 'trending-up';
    if (benchmark.percentileRank >= 60) return 'remove';
    return 'trending-down';
  };

  const formatValue = (value: number) => {
    // Format based on the metric type
    if (
      benchmark.metric.toLowerCase().includes('rate') ||
      benchmark.metric.toLowerCase().includes('percentage')
    ) {
      return `${Math.round(value)}%`;
    }
    if (
      benchmark.metric.toLowerCase().includes('velocity') ||
      benchmark.metric.toLowerCase().includes('speed')
    ) {
      return `${value.toFixed(1)}/week`;
    }
    return value.toString();
  };

  const calculateBarWidth = (value: number, max: number) => {
    return Math.min((value / max) * 100, 100);
  };

  const maxValue = Math.max(
    benchmark.userValue,
    benchmark.peerAverage,
    benchmark.topPercentile
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.metricName}>{benchmark.metric}</Text>
        <View style={styles.performanceIndicator}>
          <Ionicons
            name={getPerformanceIcon() as any}
            size={16}
            color={getPerformanceColor()}
          />
          <Text
            style={[styles.percentileRank, { color: getPerformanceColor() }]}
          >
            {benchmark.percentileRank}th percentile
          </Text>
        </View>
      </View>

      <Text style={styles.interpretation}>{benchmark.interpretation}</Text>

      <View style={styles.comparisonBars}>
        {/* User Value */}
        <View style={styles.barContainer}>
          <View style={styles.barHeader}>
            <Text style={styles.barLabel}>You</Text>
            <Text style={styles.barValue}>
              {formatValue(benchmark.userValue)}
            </Text>
          </View>
          <View style={styles.barBackground}>
            <View
              style={[
                styles.barFill,
                {
                  width: `${calculateBarWidth(benchmark.userValue, maxValue)}%`,
                  backgroundColor: '#6750A4',
                },
              ]}
            />
          </View>
        </View>

        {/* Peer Average */}
        <View style={styles.barContainer}>
          <View style={styles.barHeader}>
            <Text style={styles.barLabel}>Peer Average</Text>
            <Text style={styles.barValue}>
              {formatValue(benchmark.peerAverage)}
            </Text>
          </View>
          <View style={styles.barBackground}>
            <View
              style={[
                styles.barFill,
                {
                  width: `${calculateBarWidth(benchmark.peerAverage, maxValue)}%`,
                  backgroundColor: '#9CA3AF',
                },
              ]}
            />
          </View>
        </View>

        {/* Top Percentile */}
        <View style={styles.barContainer}>
          <View style={styles.barHeader}>
            <Text style={styles.barLabel}>Top 10%</Text>
            <Text style={styles.barValue}>
              {formatValue(benchmark.topPercentile)}
            </Text>
          </View>
          <View style={styles.barBackground}>
            <View
              style={[
                styles.barFill,
                {
                  width: `${calculateBarWidth(benchmark.topPercentile, maxValue)}%`,
                  backgroundColor: '#10B981',
                },
              ]}
            />
          </View>
        </View>
      </View>

      {benchmark.improvementPotential > 0 && (
        <View style={styles.improvementContainer}>
          <Ionicons name='arrow-up-circle-outline' size={16} color='#F59E0B' />
          <Text style={styles.improvementText}>
            {benchmark.improvementPotential}% improvement potential
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  metricName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  performanceIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  percentileRank: {
    fontSize: 12,
    fontWeight: '600',
  },
  interpretation: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 16,
    lineHeight: 18,
  },
  comparisonBars: {
    gap: 12,
  },
  barContainer: {
    gap: 4,
  },
  barHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  barLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#4B5563',
  },
  barValue: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1F2937',
  },
  barBackground: {
    height: 8,
    backgroundColor: '#F3F4F6',
    borderRadius: 4,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 4,
  },
  improvementContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    gap: 6,
  },
  improvementText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#F59E0B',
  },
});
